/**
 * mobile-menu-contract.js — the single declared registry for the mobile-menu
 * fragment system (Workstream B). Pure data + tiny DOM-only predicates; every
 * consumer (parser extraction, merge engine, corpus gate, UI eligibility)
 * DERIVES from this file — never give one its own copy (the motion-patterns
 * registry rule).
 *
 * Anatomy contract (verified across the catalog + the three custom art navs):
 * a CodeStitch nav's mobile drawer is the `.cs-ul-wrapper` subtree; the link
 * list `ul.cs-ul` inside it is SHARED with desktop; `.cs-toggle` (hamburger)
 * is host-owned and never part of a fragment; activation is `.cs-active` on
 * the section root + toggle + dropdowns, `body.cs-open` scroll lock, and
 * aria-expanded mirroring.
 *
 * Eligibility (review-measured: of 439 root navigation records, 350 match
 * this shape and 89 do not — one nav has two cs-ul lists, many lack wrapper
 * or toggle): a fragment may only be applied to (or extracted from) a nav
 * with EXACTLY ONE wrapper, ONE link list, and ONE toggle. Nonconforming
 * navs degrade to "not eligible" with a reason — never to a broken merge.
 */

export const FRAGMENT_CONTRACT_VERSION = 1;

export const FRAGMENT_CATEGORIES = ['mobile-menu'];

export function isFragmentCategory(category) {
  return FRAGMENT_CATEGORIES.includes(category);
}

/** Drawer structure classes every CodeStitch-style nav shares. */
export const CANONICAL_DRAWER_CLASSES = [
  'cs-ul-wrapper',
  'cs-ul',
  'cs-li',
  'cs-li-link',
  'cs-dropdown',
  'cs-drop-ul',
  'cs-drop-li',
  'cs-drop-link'
];

/** The activation contract fragments must honor (JS-free by design). */
export const ACTIVATION_CONTRACT = {
  rootActiveClass: 'cs-active',
  toggleClass: 'cs-toggle',
  bodyOpenClass: 'cs-open',
  listIdPrefix: 'cs-expanded',
  dropdownClass: 'cs-dropdown'
};

/** Marker attributes stamped on fragment-owned markup. */
export const MARKER_ATTRS = {
  chrome: 'data-pb-mm-chrome', // fragment chrome roots (removed on re-swap)
  deco: 'data-pb-mm-deco', // per-link decoration elements
  slot: 'data-pb-mm-slot', // the <ul> placeholder in fragment HTML
  label: 'data-pb-mm-label', // per-link label slot inside item templates
  fragment: 'data-pb-mm-fragment' // stamped with the fragment's catalogKey
};

/** Tokens item/dropdown templates may use; label content is NODE-cloned. */
export const TEMPLATE_TOKENS = [
  '{{label}}',
  '{{href}}',
  '{{index}}',
  '{{indexPadded}}',
  '{{submenu}}'
];

/**
 * Host-shape eligibility check. `navRootEl` is the nav section's root element
 * (browser DOM or jsdom). Returns { eligible, reasons } where reasons are
 * stable machine-readable slugs the UI turns into copy.
 */
export function checkNavEligibility(navRootEl) {
  const reasons = [];
  if (!navRootEl || typeof navRootEl.querySelectorAll !== 'function') {
    return { eligible: false, reasons: ['no-nav-root'] };
  }
  const wrappers = navRootEl.querySelectorAll('.cs-ul-wrapper');
  const lists = navRootEl.querySelectorAll('ul.cs-ul');
  const toggles = navRootEl.querySelectorAll(`.${ACTIVATION_CONTRACT.toggleClass}`);
  if (wrappers.length === 0) reasons.push('no-wrapper');
  if (wrappers.length > 1) reasons.push('multiple-wrappers');
  if (lists.length === 0) reasons.push('no-link-list');
  if (lists.length > 1) reasons.push('multiple-link-lists');
  if (toggles.length === 0) reasons.push('no-toggle');
  if (toggles.length > 1) reasons.push('multiple-toggles');
  // The link list must live INSIDE the wrapper — the swap boundary depends on it.
  if (wrappers.length === 1 && lists.length === 1 && !wrappers[0].contains(lists[0])) {
    reasons.push('list-outside-wrapper');
  }
  return { eligible: reasons.length === 0, reasons };
}

/**
 * Convenience for UI surfaces holding a nav's HTML string: parse (DOMParser —
 * browser or jsdom-global) and eligibility-check in one call. Returns
 * { eligible, reasons, reasonCopy } where reasonCopy is the joined human copy.
 */
export function checkNavHtmlEligibility(html) {
  const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
  const result = checkNavEligibility(doc.body.firstElementChild?.firstElementChild || null);
  const reasonCopy = result.reasons
    .map((reason) => ELIGIBILITY_REASON_COPY[reason])
    .filter(Boolean)
    .join(' ');
  return { ...result, reasonCopy };
}

/** Human copy for eligibility reasons (UI disabled-state messaging). */
export const ELIGIBILITY_REASON_COPY = {
  'no-nav-root': 'No navigation section found',
  'no-wrapper': 'This navigation has no drawer container (.cs-ul-wrapper)',
  'multiple-wrappers': 'This navigation has more than one drawer container',
  'no-link-list': 'This navigation has no link list (ul.cs-ul)',
  'multiple-link-lists': 'This navigation has two link lists',
  'no-toggle': 'This navigation has no hamburger toggle (.cs-toggle)',
  'multiple-toggles': 'This navigation has more than one hamburger toggle',
  'list-outside-wrapper': 'The link list sits outside the drawer container'
};
